import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { WifiOff, BookOpen, Target, ArrowRight, CheckCircle2, HelpCircle, Lightbulb, FlaskConical, LineChart } from "lucide-react";

const REFERENTIEL = [
  { year: "3e primaire", theme: "La Découverte", skills: ["Trier des informations", "Découvrir le matériel", "Premiers pas sécurité"] },
  { year: "4e primaire", theme: "Je deviens acteur", skills: ["Construire un algorithme", "Créer un contenu", "Publier de façon responsable"] },
  { year: "5e primaire", theme: "L'analyse critique", skills: ["Vérifier une information", "Citer ses sources", "Programmer un parcours"] },
  { year: "6e primaire", theme: "Le citoyen numérique", skills: ["Communiquer avec respect", "Protéger sa vie privée", "Adopter une charte"] },
];

const STEPS = [
  { n: "01", label: "Questionnement", icon: HelpCircle, c: "bg-sky-100 text-sky-600", title: "La curiosité comme moteur", text: "Toute exploration du numérique commence par une interrogation. Pourquoi l'écran réagit-il au toucher ? Comment les informations voyagent-elles d'un bout à l'autre de la planète ? Pourquoi deux sites web affichent-ils des versions différentes d'une même actualité ? Qu'advient-il de mes données personnelles lorsque je les partage ? L'intelligence artificielle pense-t-elle comme nous ?… Nos ateliers partent des questions que les enfants se posent au quotidien pour les guider vers des réponses concrètes." },
  { n: "02", label: "Hypothèse", icon: Lightbulb, c: "bg-amber-100 text-amber-600", title: "Bien plus qu'une intuition", text: "Face à un phénomène numérique, il s'agit d'avancer une explication provisoire : l'hypothèse. Celle-ci ne se limite pas à une simple supposition : elle pose une relation entre une cause et un effet. Par exemple, « Si je raccourcis ce mot de passe, il devient plus vulnérable au piratage » ou « En modifiant l'ordre des blocs de ce programme, l'affichage final sera différent ». L'hypothèse est le point de départ de toute investigation." },
  { n: "03", label: "Expérimentation", icon: FlaskConical, c: "bg-rose-100 text-rose-600", title: "Tester pour valider", text: "Une fois l'hypothèse clairement énoncée par les enfants, place à l'expérimentation. Celle-ci doit être rigoureuse : elle ne modifie qu'un seul facteur à la fois afin d'isoler la variable testée. Les expériences prennent des formes variées : programmer une séquence d'instructions, simuler un échange de données, jouer le rôle d'un algorithme en groupe, ou encore comparer plusieurs sources d'information sur un même sujet." },
  { n: "04", label: "Analyse des résultats", icon: LineChart, c: "bg-emerald-100 text-emerald-600", title: "Vérifier, confronter, recommencer", text: "L'expérimentation livre ses résultats : confirment-ils ou infirment-ils l'hypothèse ? Les observations sont comparées, notées et partagées collectivement pour enrichir la réflexion. Si les résultats ne correspondent pas aux attentes, aucune déception : c'est l'occasion de formuler une nouvelle hypothèse et de repartir pour un nouveau cycle d'investigation. Car dans le numérique comme dans la science, l'erreur est une étape précieuse du chemin vers la compréhension." },
];

export default function Approche() {
  return (
    <div data-testid="approche-page">
      <section className="bg-gradient-to-b from-sky-50 to-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-sky-500">Notre univers · Approche</span>
          <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl text-slate-900 mt-2 max-w-3xl">
            Une pédagogie active !
          </h1>
        </div>
      </section>

      {/* Approche - intro */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="max-w-3xl">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-sky-500">Approche</span>
          <div className="mt-4 space-y-4 text-lg text-slate-600 leading-relaxed">
            <p>En plaçant les enfants au cœur de la démarche, nous les invitons à observer, à manipuler et à relever des défis concrets. Notre objectif est double : répondre aux interrogations qu'ils formulent sur le numérique, et en faire émerger de nouvelles. Nous sommes convaincus que la phase du « pourquoi » est fondamentale dans le développement de l'enfant. Nos activités numériques sont conçues pour nourrir cette curiosité naturelle et l'aider à se structurer face aux phénomènes technologiques et à leurs enjeux.</p>
            <p>Qu'il s'agisse d'une intervention en classe, d'un séjour découverte, d'une sortie scolaire, d'un stage pendant les vacances, d'un anniversaire ou d'un événement ponctuel, chaque enfant bénéficie de son propre équipement (ordinateur, tablette ou support « hors ligne ») pour accomplir une mission ludique, procéder à une observation attentive ou mener une expérience concrète. Chacun avance à sa cadence, en empruntant le chemin de raisonnement qui lui est propre !</p>
          </div>
        </div>
      </section>

      {/* La demarche d'investigation */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-4">
        <h2 className="font-heading font-extrabold text-3xl sm:text-4xl text-slate-900 mb-8">La démarche d'investigation</h2>
        <div className="grid sm:grid-cols-2 gap-6">
          {STEPS.map((s, i) => (
            <motion.div
              key={s.n}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: (i % 2) * 0.08 }}
              className="bg-white rounded-3xl border border-slate-100 shadow-sm p-7"
              data-testid={`step-${s.n}`}
            >
              <div className="flex items-center gap-4 mb-4">
                <span className={`grid place-items-center w-14 h-14 rounded-2xl ${s.c}`}>
                  <s.icon className="w-7 h-7" />
                </span>
                <div>
                  <span className="font-heading font-black text-2xl text-slate-200">{s.n}</span>
                  <p className="text-xs font-bold uppercase tracking-wide text-slate-400">{s.label}</p>
                </div>
              </div>
              <h3 className="font-heading font-extrabold text-xl text-slate-900">{s.title}</h3>
              <p className="text-sm text-slate-500 mt-2 leading-relaxed">{s.text}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-slate-900 rounded-[2.5rem] p-10 sm:p-14 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/20 text-emerald-300 text-sm font-bold">
              <WifiOff className="w-4 h-4" /> Méthodologie hors ligne
            </span>
            <h2 className="font-heading font-extrabold text-3xl sm:text-4xl text-white mt-4">
              Le "Plan B" : apprendre même sans écran
            </h2>
            <p className="mt-4 text-slate-300 leading-relaxed">
              Une panne technique ne doit jamais gâcher un atelier. Chaque activité
              dispose d'une version débranchée : jeux de rôle, cartes, gommettes et
              manipulation papier. Les concepts restent les mêmes.
            </p>
          </div>
          <div className="grid gap-4">
            {["Jeux de rôle et mises en situation", "Cartes d'instructions et algorithmes papier", "Badges et gommettes de progression", "Aucune dépendance à Internet"].map((t) => (
              <div key={t} className="flex items-center gap-3 bg-slate-800 rounded-2xl p-4">
                <CheckCircle2 className="w-6 h-6 text-emerald-400 shrink-0" />
                <span className="text-slate-200 font-medium">{t}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="max-w-2xl mb-10">
          <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-sky-500">
            <BookOpen className="w-4 h-4" /> Le référentiel FMTTN
          </span>
          <h2 className="font-heading font-extrabold text-3xl sm:text-4xl text-slate-900 mt-2">
            Des compétences année par année
          </h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {REFERENTIEL.map((r, i) => (
            <motion.div
              key={r.year}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08 }}
              className="bg-white rounded-3xl border border-slate-100 shadow-sm p-6"
            >
              <span className="grid place-items-center w-12 h-12 rounded-2xl bg-sky-100 text-sky-600 mb-4">
                <Target className="w-6 h-6" />
              </span>
              <p className="text-xs font-bold uppercase tracking-wide text-slate-400">{r.year}</p>
              <h3 className="font-heading font-extrabold text-lg text-slate-900 mt-1 mb-4">{r.theme}</h3>
              <ul className="space-y-2">
                {r.skills.map((s) => (
                  <li key={s} className="flex items-start gap-2 text-sm text-slate-600">
                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-amber-400 shrink-0" /> {s}
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>
        <div className="text-center mt-12">
          <Link to="/nos-activites" data-testid="approche-to-activities" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-sky-500 text-white font-bold hover:bg-sky-600 hover:scale-105 transition-[transform,background-color]">
            Voir les ateliers <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
}
